import { useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';
import { ComboBox } from '../../components/ComboBox';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';

// Troca a CATEGORIA dos produtos marcados na lista ([DA-761], pedido dele 26/09/2026). A categoria
// abre VAZIA de proposito: a escolha e' dele, e o botao so acende depois dela. A previa mostra
// `atual -> nova` linha a linha antes de gravar.

export interface ProdutoCategoria {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  categoria_id: number | null;
  categoria_nome: string | null;
}

interface Props {
  produtos: ProdutoCategoria[];
  categorias: { id: number; nome: string }[];
  onFechar: () => void;
  onConcluido: () => void;
}

export function ModalCategoriaLote({ produtos, categorias, onFechar, onConcluido }: Props) {
  const toast = useToast();
  const [categoriaId, setCategoriaId] = useState<number | ''>('');
  const [salvando, setSalvando] = useState(false);

  const nomeNova = categorias.find((c) => c.id === categoriaId)?.nome ?? null;
  const mudam = useMemo(
    () => (categoriaId === '' ? 0 : produtos.filter((p) => p.categoria_id !== categoriaId).length),
    [produtos, categoriaId],
  );

  async function confirmar() {
    if (categoriaId === '' || mudam === 0) return;
    setSalvando(true);
    try {
      const r = await api.patch<{ atualizados: number; sem_mudanca: number }>('/api/produtos/categoria-lote', {
        produto_ids: produtos.map((p) => p.id),
        categoria_id: categoriaId,
      });
      toast.sucesso(`${r.atualizados} produto(s) agora em ${nomeNova}.`);
      onConcluido();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal aberto onFechar={onFechar} titulo="Alterar categoria em lote" largura={700}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>Nova categoria dos {produtos.length} produto(s):</span>
        <div style={{ minWidth: 260 }}>
          <ComboBox
            opcoes={categorias.map((c) => ({ value: c.id, label: c.nome }))}
            value={categoriaId}
            onChange={(v) => setCategoriaId(v === '' ? '' : Number(v))}
            placeholder="Escolha a categoria..."
          />
        </div>
      </div>

      <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border, #e5e7eb)', borderRadius: 6 }}>
        <table className="tabela" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th style={{ width: 64 }}>COD.</th>
              <th>DESCRIÇÃO</th>
              <th>CATEGORIA</th>
            </tr>
          </thead>
          <tbody>
            {produtos.map((p) => {
              const muda = categoriaId !== '' && p.categoria_id !== categoriaId;
              return (
                <tr key={p.id}>
                  <td><span className="codigo-curto">{p.codigo_interno}</span></td>
                  <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.descricao}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {muda
                      ? <span><span className="muted">{p.categoria_nome ?? '(sem)'}</span> → <strong>{nomeNova}</strong></span>
                      : (p.categoria_nome ?? <span className="muted">(sem)</span>)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 14 }}>
        {categoriaId === '' && <span className="muted" style={{ fontSize: 13, marginRight: 'auto' }}>Escolha a categoria para aplicar.</span>}
        {categoriaId !== '' && mudam === 0 && <span className="muted" style={{ fontSize: 13, marginRight: 'auto' }}>Todos já estão nessa categoria.</span>}
        <button type="button" className="btn-secundario" onClick={onFechar} disabled={salvando}>Cancelar</button>
        <button type="button" className="btn-primario" onClick={confirmar} disabled={categoriaId === '' || mudam === 0 || salvando}>
          {salvando ? 'Aplicando...' : `Aplicar em ${mudam} produto(s)`}
        </button>
      </div>
    </Modal>
  );
}
